/* eslint-disable jsx-a11y/control-has-associated-label */
// In use by Data Discovery as well as LogMiner for their Zoomed Image navigation that has full URL support
// Also has zoom/pan capabilities.

import { FC, MouseEventHandler, useCallback, useEffect, useRef } from 'react';
import {
  Button,
  ButtonProps,
  Drawer,
  Skeleton,
  Spin,
  ConfigProvider,
  theme,
} from 'antd';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { CenteredBox } from 'packages/lib/common-ui/centeredBox';
import { REQ_STATUS } from 'packages/lib/common-types/types';
import { replaceHistorySearch } from 'packages/lib/utils/url';
import useDialogOpenState from 'packages/lib/utils/hooks/use-dialog-open-state';
import {
  CloseOutlined,
  LeftOutlined,
  MinusOutlined,
  PlusOutlined,
  RightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from '@ant-design/icons';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

import styled from '@emotion/styled';
import { css } from '@emotion/css';
import { AnnotatedImage } from 'packages/data-discovery/results/annotated-image';
import { TextSearchResultType } from 'packages/lib/api/logminer/contract/data-discovery-text-search-results';

const SIDE_PANEL_WIDTH = 420;
export const BASE_SCALE_RATIO = 1;
export const WHEEL_MAX_SCALE_RATIO = 1; // The maximum zoom ratio when the mouse zooms in

const styles: Record<string, React.CSSProperties> = {
  floatingImgContainer: {
    position: 'absolute',
    width: `calc(100% - ${SIDE_PANEL_WIDTH}px)`,
    height: '100%',
    top: '0px',
    left: '0px',
    zIndex: 900,
  },
  controlsBackground: {
    position: 'absolute',
    top: 0,
    right: 0,
    height: '60px',
    width: '100%',
    backgroundColor: 'rgba(0,0,0,0.3)',
    zIndex: 1999,
  },
  controlsButton: {
    position: 'absolute',
    top: 0,
    right: 0,
    margin: '10px 10px',
    zIndex: 2000,
    borderColor: 'rgba(0,0,0,0)',
  },
};

/* For some reason the default Ant ghost button disappears entirely when over a black background. We give it a proper light gray here. */
const antGhostButton = css`
  &.ant-btn[disabled],
  &.ant-btn[disabled]:hover {
    color: rgba(255, 255, 255, 0.4);
  }
`;

export const AnimatedBackground = styled.div`
  @keyframes FadeDivIn {
    0% {
      opacity: 0.2;
    }
    100% {
      opacity: 1;
    }
  }

  @keyframes ZoomImageIn {
    0% {
      -webkit-transform: scale(0.3);
    }
    100% {
      -webkit-transform: scale(1);
    }
  }

  .backdropFadeIn {
    display: 'block';
    animation: FadeDivIn 0.4s;
    animation-timing-function: ease-in;
    animation-iteration-count: 1;
    background-color: rgba(0, 0, 0, 0.7);
  }

  .zoomImageIn {
    display: 'block';
    animation: ZoomImageIn 0.4s;
    animation-timing-function: ease-in;
    animation-iteration-count: 1;
  }
`;

interface LinkButtonProps {
  link: string;
  disabled?: boolean;
  linkOnClick?: MouseEventHandler<HTMLAnchorElement>;
}

type ResetTransformFunc = () => void;

// Simple Ant Button wrapper that lets us have icon buttons that are still links
// Allows us to still disable the button (AND the link!) when disabled={true}
export const LinkButton: FC<
  React.PropsWithChildren<LinkButtonProps & ButtonProps>
> = ({ link, linkOnClick, disabled, children, ...buttonProps }) => {
  return disabled ? (
    <Button disabled={true} {...buttonProps}>
      {children}
    </Button>
  ) : (
    <Link to={link} onClick={linkOnClick}>
      <Button {...buttonProps}>{children}</Button>
    </Link>
  );
};

interface FilterResultZoomedImageProps {
  imageSrc: string | null;
  logName: string | null;
  cameraChannel: string | null;
  imageStatus: REQ_STATUS;
  panelHeader: React.ReactNode;
  metadataComponent: JSX.Element | null;
  isOpen: boolean;
  prevImageURLSearchStr: string;
  nextImageURLSearchStr: string;
  closeSidepanel: () => void;
  annotationsRed?: TextSearchResultType['bounding_box'];
  annotationsBlue?: TextSearchResultType['bounding_box'];
  selected?: boolean;
  onSelect?: (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => void;
}

export const FilterResultZoomedImage: FC<
  React.PropsWithChildren<FilterResultZoomedImageProps>
> = ({
  isOpen,
  imageSrc,
  logName,
  cameraChannel,
  imageStatus,
  panelHeader,
  metadataComponent,
  annotationsRed,
  annotationsBlue,
  prevImageURLSearchStr,
  nextImageURLSearchStr,
  selected,
  closeSidepanel,
  onSelect,
}) => {
  const location = useLocation();
  const history = useHistory();
  const { isAnyDialogOpen } = useDialogOpenState();
  const aDialogIsOpen = isAnyDialogOpen();
  const resetTransformRef = useRef<ResetTransformFunc | null>(null);

  const imageExists = imageSrc && imageStatus === REQ_STATUS.success;

  // Esc key closes Image Details panel if it's open. Left/right arrow keys navigate.
  const keyHandler = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        closeSidepanel();
        return false;
      }
      if (e.key === 'ArrowRight') {
        if (nextImageURLSearchStr.length > 0) {
          replaceHistorySearch(history, location, nextImageURLSearchStr);
          if (resetTransformRef.current) resetTransformRef.current();
        }
        return false;
      }
      if (e.key === 'ArrowLeft') {
        if (prevImageURLSearchStr.length > 0) {
          replaceHistorySearch(history, location, prevImageURLSearchStr);
          if (resetTransformRef.current) resetTransformRef.current();
        }
        return false;
      }
      return undefined;
    },
    [
      closeSidepanel,
      history,
      location,
      nextImageURLSearchStr,
      prevImageURLSearchStr,
      resetTransformRef,
    ]
  );

  useEffect(() => {
    if (!isOpen || aDialogIsOpen) {
      window.removeEventListener('keydown', keyHandler);
    } else {
      window.addEventListener('keydown', keyHandler);
    }

    return (): void => {
      window.removeEventListener('keydown', keyHandler);
    };
  }, [isOpen, keyHandler, aDialogIsOpen]);

  const ignoreClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
  };
  const dummyKeyPressHandler = (): boolean | undefined => {
    return undefined;
  };

  return (
    <>
      <Drawer
        closable={false}
        open={isOpen}
        width={SIDE_PANEL_WIDTH}
        title={panelHeader}
        mask={false}
        onClose={closeSidepanel}
      >
        {metadataComponent}
      </Drawer>
      {isOpen && (
        <AnimatedBackground
          style={styles.floatingImgContainer}
          role="button"
          tabIndex={0}
          onClick={(e): void => {
            closeSidepanel();
          }}
        >
          <TransformWrapper wheel={{ step: 0.15 }}>
            {({ zoomIn, zoomOut, resetTransform, state }): JSX.Element => {
              resetTransformRef.current = resetTransform;
              return (
                <div style={{ height: '100%' }} className="backdropFadeIn">
                  <div style={{ height: '100%' }} className="zoomImageIn">
                    <ConfigProvider
                      theme={{
                        algorithm: [theme.compactAlgorithm],
                      }}
                    >
                      <div style={styles.controlsBackground}>
                        {onSelect && (
                          <Button
                            data-testid={
                              selected ? 'dd-remove-image' : 'dd-add-image'
                            }
                            ghost
                            shape="circle"
                            size="large"
                            style={{ ...styles.controlsButton, right: 150 }}
                            className={antGhostButton}
                            icon={
                              selected ? <MinusOutlined /> : <PlusOutlined />
                            }
                            onClick={onSelect}
                            disabled={!imageExists}
                          />
                        )}
                        <Button
                          ghost
                          shape="circle"
                          size="large"
                          style={{ ...styles.controlsButton, right: 100 }}
                          className={antGhostButton}
                          icon={<ZoomOutOutlined />}
                          onClick={(e): void => {
                            zoomOut();
                            e.stopPropagation();
                          }}
                          disabled={state.scale === 1.0 || !imageExists}
                        />
                        <Button
                          ghost
                          shape="circle"
                          size="large"
                          style={{ ...styles.controlsButton, right: 50 }}
                          className={antGhostButton}
                          icon={<ZoomInOutlined />}
                          onClick={(e): void => {
                            zoomIn();
                            e.stopPropagation();
                          }}
                          disabled={!imageExists}
                        />
                        <Button
                          ghost
                          shape="circle"
                          size="large"
                          style={{ ...styles.controlsButton, right: 0 }}
                          className={antGhostButton}
                          icon={<CloseOutlined />}
                          onClick={(e): void => {
                            closeSidepanel();
                            e.stopPropagation();
                          }}
                        />
                      </div>
                    </ConfigProvider>
                    <CenteredBox
                      style={{
                        position: 'relative',
                        backgroundColor: 'rgba(0,0,0,0)',
                      }}
                    >
                      <TransformComponent>
                        {imageExists && (
                          <div
                            role="button"
                            tabIndex={0}
                            onClick={ignoreClick}
                            style={{
                              width: '100%',
                              overflow: 'clip',
                            }}
                            onKeyPress={dummyKeyPressHandler}
                          >
                            <CenteredBox>
                              <AnnotatedImage
                                annotationsRed={annotationsRed ?? []}
                                annotationsBlue={annotationsBlue ?? []}
                                src={imageSrc}
                                zoom={state.scale}
                              />
                            </CenteredBox>
                          </div>
                        )}
                      </TransformComponent>
                      {!imageExists && (
                        <>
                          {imageStatus === REQ_STATUS.pending ? (
                            <CenteredBox>
                              <Spin size="large" />
                            </CenteredBox>
                          ) : (
                            <CenteredBox>
                              <Skeleton.Image />

                              <div style={{ color: 'white' }}>
                                {logName}
                                <br />
                                {cameraChannel}
                                <br />
                                <br />
                                Could not load image. (Is it in AWS Glacier?)
                              </div>
                            </CenteredBox>
                          )}
                        </>
                      )}
                    </CenteredBox>
                    {/* Left and right buttons for Next / Prev image */}
                    <LinkButton
                      link={location.pathname + prevImageURLSearchStr}
                      linkOnClick={(e): void => {
                        resetTransform();
                        e.stopPropagation();
                      }}
                      icon={<LeftOutlined />}
                      size="large"
                      shape="circle"
                      ghost
                      disabled={prevImageURLSearchStr === ''}
                      style={{
                        position: 'absolute',
                        left: 30,
                        top: 'calc(50% - 20px)',
                      }}
                    />
                    <LinkButton
                      link={location.pathname + nextImageURLSearchStr}
                      linkOnClick={(e): void => {
                        resetTransform();
                        e.stopPropagation();
                      }}
                      ghost
                      shape="circle"
                      icon={<RightOutlined />}
                      size="large"
                      onClick={(e): void => {
                        e.currentTarget.blur();
                      }}
                      disabled={nextImageURLSearchStr === ''}
                      style={{
                        position: 'absolute',
                        right: 30,
                        top: 'calc(50% - 20px)',
                      }}
                    />
                  </div>
                </div>
              );
            }}
          </TransformWrapper>
        </AnimatedBackground>
      )}
    </>
  );
};

export default FilterResultZoomedImage;
